import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import CloseSearchIcon from '../../assets/close_search.svg';
import SearchIcon from '../../assets/search_icon_v2.svg';
import useDebounce from '../../hooks/useDebounce';
import { analytics } from '../../services/analytics';
import { useStores } from '../../stores/useStores';

import styles from './SearchButtonV2.module.scss';

const SearchButton = observer(() => {
  const { gamesSearchStore } = useStores();
  const { t } = useTranslation();
  const { isSearchInputOpenV2, searchTerm } = gamesSearchStore;
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearchTerm = useDebounce<string>(inputValue, 500);

  useEffect(() => {
    if (searchTerm !== inputValue || inputRef.current?.value !== searchTerm) {
      if (inputRef.current) {
        inputRef.current.value = searchTerm;
      }
      setInputValue(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    gamesSearchStore.setSearchTerm(debouncedSearchTerm);
  }, [gamesSearchStore, debouncedSearchTerm]);

  // Set the cursor position to the end of the input value
  const handleFocus = () => {
    const input = inputRef.current;
    if (input) {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  };

  const cleanText = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setInputValue('');
    gamesSearchStore.cleanSearch();
  };

  const closeSearch = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    gamesSearchStore.closeSearch();
  };

  const handleClick = () => {
    if (inputRef.current) {
      if (!isSearchInputOpenV2) {
        inputRef.current.focus();
        gamesSearchStore.openSearch();
      } else {
        cleanText();
        closeSearch();
      }
      void analytics.sendEvent('game_search', {
        event_action: 'click',
      });
      void analytics.sendEvent('search_provider', {
        event_category: 'lobby_page',
        event_action: 'click',
      });
    }
  };

  const onCrossClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inputValue) {
      cleanText();
      return;
    }
    closeSearch();
    cleanText();
  };

  const [width, setWidth] = useState(0);
  const span = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const contentWidth = (span.current?.offsetWidth || 0) + 85;
    setWidth(contentWidth < 200 ? 200 : contentWidth);
  }, [inputValue]);

  useEffect(() => {
    if (!isSearchInputOpenV2) {
      cleanText();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchInputOpenV2]);

  return (
    <div
      className={classNames(styles.filters__search, { [styles.active]: isSearchInputOpenV2 })}
      style={isSearchInputOpenV2 ? { minWidth: width } : {}}
      onClick={handleClick}
    >
      <span className={styles.iconWrapper}>
        <img alt="search icon" className={styles.searchIcon} src={SearchIcon} />
      </span>
      <span ref={span} className={styles.hiddenContent}>
        {inputValue}
      </span>
      <input
        ref={inputRef}
        autoComplete="off"
        className={classNames(styles.searchInput, { [styles.active]: isSearchInputOpenV2 })}
        data-testid="lobby-search-box"
        placeholder={t('search.startYourSearch')}
        type="search"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={handleFocus}
      />
      {isSearchInputOpenV2 && (
        <span className={styles.iconWrapper} onClick={onCrossClick}>
          <img alt="close search" className={styles.searchIcon} src={CloseSearchIcon} />
        </span>
      )}
    </div>
  );
});

export default SearchButton;
